import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {MatTabChangeEvent, MatTabGroup} from '@angular/material/tabs';
import {BehaviorSubject, EMPTY, forkJoin, iif, merge, Observable, of, Subject} from 'rxjs';
import {UserAreaData} from '@enum/common/user-area-data';
import {AuthService} from '@service/auth/auth.service';
import {OrderService} from '@service/order/order.service';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {UserService} from '@service/user/user.service';
import {HeaderStep} from '@enum/header.step';
import {HeaderService} from '@service/header/header.service';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NavigationService} from '@service/navigation/navigation.service';
import {MatDialog} from '@angular/material/dialog';
import {CommonService} from '@service/common/common.service';
import {UserAreaFilter} from '@enum/user-area/user-area-filter';
import {Order} from '@interface/order/order/order';
import {ModalService} from '@service/modal/modal.service';
import {ClientSearch} from '@interface/order/client/client-search';

@Component({
    selector: 'app-user-area',
    templateUrl: './user-area.component.html',
    styleUrls: ['./user-area.component.scss'],
    standalone: false
})
export class UserAreaComponent implements OnInit, OnDestroy {
  @ViewChild('tabGroup') tabGroup: MatTabGroup | undefined;
  @ViewChild('proformaFormModal') proformaFormModal: TemplateRef<any> | undefined;
  public UserAreaFilter = UserAreaFilter;
  public filterAreaForm = this.fb.group({
    id: [],
    code: [],
    name: [],
    email: [],
    intercompanyId: [],
    agencyId: [],
    showroomId: [],
    customerChannelId: [],
    shopVendorId: [],
    countryId: [],
    customerName: [],
    status: [],
    categoryId: [],
    creationDateFilterStart: [],
    creationDateFilterEnd: [],
    etoStatus: []
  });
  private searchAction = new BehaviorSubject<any | undefined>(undefined);
  public search$ = this.searchAction.asObservable();
  public selectedTab: UserAreaData = UserAreaData.CUSTOMER;
  public userArea = UserAreaData;
  public states: { name: string, value: string }[] = [];
  public etoStates: { name: string, value: string }[] = [];
  public categories: { name: string, value: number }[] = [];
  public intercompanies: { name: string, value: number }[] = [];
  public agencies: { name: string, value: number }[] = [];
  public showrooms: { name: string, value: number }[] = [];
  public countries: { name: string, value: number }[] = [];
  public channels: { name: string, value: number }[] = [];
  public shopVendors: { name: string, value: number }[] = [];
  public proformaForm = new UntypedFormGroup({
    customerId: new UntypedFormControl(null, [Validators.required])
  });

  public lists: { customer: ClientSearch[] } = {
    customer: []
  };
  private oldForm: any;
  private destroy$ = new Subject();

  constructor(private authService: AuthService,
              private orderService: OrderService, private userService: UserService, private modalService: ModalService,
              private headerService: HeaderService, public fb: UntypedFormBuilder, private dialog: MatDialog,
              private commonService: CommonService, private navigation: NavigationService) {
  }

  ngOnInit(): void {
    if (this.authService.currentUserPermission.hasMoreClients) {
      this.selectedTab = UserAreaData.CUSTOMER;
    } else {
      this.selectedTab = UserAreaData.ORDER;
    }
    this.updateList();
    this.search();
    this.searchOnSelectionChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  public reset(): void {
    this.filterAreaForm.reset(undefined, {emitEvent: false});
    this.search();
  }

  public openCreateOrderModal(): void {
    const dialogRef = this.dialog.open(this.proformaFormModal as TemplateRef<any>, {
      id: 'proformaFormModal',
      width: '40vw',
      height: '30vh'
    });
    const user$ = this.userService.searchCustomer({lightSearch: true})
      .pipe(
        tap((data) => {
          this.lists.customer = data?.customers;
          this.proformaForm.patchValue({
            customerId: this.orderService.currentClient?.id || null
          });
          this.proformaForm.updateValueAndValidity();
        })
      );
    forkJoin({
      dialogResp: dialogRef.afterClosed(),
      userResp: user$
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        return this.searchAction.next(this.oldForm);
      });
  }

  public createOrder(): void {
    this.orderService.orderCreate({
      customerId: this.proformaForm.controls.customerId.value,
      createEmpty: true
    }).pipe(
      switchMap((order) => this.orderService.workWithThisOrder(order)),
      takeUntil(this.destroy$)
    )
      .subscribe(() => {
        this.dialog.getDialogById('proformaFormModal')?.close();
      });
  }

  public registerNewUser(): void {
    this.modalService
      .openRegisterModal()
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((created) => {
        if (created) {
          this.searchAction.next(this.oldForm);
        }
      });
  }

  public changeTab(tabChangeEvent: MatTabChangeEvent): void {
    if (tabChangeEvent?.tab?.ariaLabel && (tabChangeEvent?.tab?.ariaLabel !== UserAreaData.CATALOG)) {
      this.selectedTab = tabChangeEvent?.tab?.ariaLabel as UserAreaData;
      this.reset();
    } else if (tabChangeEvent?.tab?.ariaLabel && (tabChangeEvent?.tab?.ariaLabel === UserAreaData.CATALOG)) {
      this.headerService.header.next(HeaderStep.HOME);
      this.navigation.goToCatalogList(false);
    }
  }

  public search(): void {
    this.oldForm = this.prepareSearchParams();
    this.searchAction.next(this.oldForm);
  }

  public currentFilters(): { key: string, keyT: string, value: string }[] {
    const listToSend: { key: string, keyT: string, value: string }[] = [];
    Object.keys(this.oldForm)
      .filter((key: string) => key !== 'type' && this.oldForm[key])
      .forEach((key: string) => {
        let keyT = '';
        let value = this.oldForm[key];
        switch (key) {
          case 'name':
            keyT = 'NOME';
            if ([UserAreaData.ORDER, UserAreaData.ETO].includes(this.oldForm.type)) {
              keyT = 'CODICE';
            }
            break;
          case 'customerName':
            keyT = 'NOME_E_COGNOME';
            break;
          case 'code':
            keyT = 'CODICE';
            break;
          case 'categoryId':
            keyT = 'CATEGORIA';
            value = this.categories.find((el) => el.value === value)?.name;
            break;
          case 'status':
            keyT = 'STATO';
            const currentState = this.states.find((el) => el.value === value);
            value = currentState?.name || value;
            break;
          case 'etoStatus':
            keyT = 'STATO';
            const currentEtoState = this.etoStates.find((el) => el.value === value);
            value = currentEtoState ? currentEtoState.name : value;
            break;
          case 'intercompanyId':
            keyT = 'INTERCOMPANY';
            value = this.intercompanies.find((el) => el.value === value)?.name;
            break;
          case 'showroomId':
            keyT = 'SHOWROOM';
            value = this.showrooms.find((el) => el.value === value)?.name;
            break;
          case 'shopVendorId':
            keyT = 'SHOPVENDOR';
            value = this.shopVendors.find((el) => el.value === value)?.name;
            break;
          case 'agencyId':
            keyT = 'AGENZIA';
            value = this.agencies.find((el) => el.value === value)?.name;
            break;
          case 'countryId':
            keyT = 'NAZIONE';
            value = this.countries.find((el) => el.value === value)?.name;
            break;
          case 'customerChannelId':
            keyT = 'CUSTOMER_CHANNEL';
            value = this.channels.find((el) => el.value === value)?.name;
            break;
          case 'creationDateFilterStart':
            keyT = 'DATA_INIZIO';
            value = new Date(value).toLocaleDateString('en-GB');
            break;
          case 'creationDateFilterEnd':
            keyT = 'DATA_FINE';
            value = new Date(value).toLocaleDateString('en-GB');
            break;
          default:
            keyT = key;
            break;
        }
        listToSend.push({key, keyT, value});
      });
    return listToSend;
  }

  public selectedCustomerEvent(customer: ClientSearch): void {
    this.orderService.workWithThisCustomer(customer);
    this.navigation.goToCatalogList(false);
  }

  public selectedOrderEvent(value: { order?: Order, orderId?: number, onlyView: boolean }): void {
    iif(
      () => !!value.order,
      !!value.order ? of(value.order) : EMPTY,
      this.orderService.orderDetail({orderId: value.orderId})
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((order: Order) => {
        if (value.onlyView) {
          this.openOrder(order);
        } else {
          this.workWithOrder(order);
        }
      });
  }

  public openOrder(order: Order): void {
    this.orderService.backupAndViewOrder(order).pipe(
      switchMap(() => this.openOrderDetail(order)),
      takeUntil(this.destroy$)
    ).subscribe((res) => {
      if (res?.shouldUpdateListWhenClose) {
        this.searchAction.next(this.oldForm);
      }
    });
  }

  public workWithOrder(order: Order): void {
    this.orderService.workWithThisOrder(order)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.orderService.refreshConfiguration();
        this.navigation.goToCatalogList(false);
      });
  }

  public openOrderDetail(order: Order): Observable<{ shouldUpdateListWhenClose: boolean } | undefined> {
    const dialogRef = this.modalService.openOrderModal({
      showSelectedOrder: order,
      isToRevertConfiguration: true
    });
    return dialogRef.afterClosed();
  }

  public canShowFilter(filter: UserAreaFilter): boolean {
    return this.authService.currentUserPermission.visibleUserAreaFilters.includes(filter);
  }

  public closeModal(): void {
    this.dialog.closeAll();
  }

  public hasMoreClients(): boolean {
    return this.authService.currentUserPermission.hasMoreClients;
  }

  private prepareSearchParams(): any {
    const form = this.filterAreaForm.getRawValue();
    return {...form, type: this.selectedTab};
  }

  private updateList(): void {
    this.commonService.interCompanyList().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.intercompanies = response.map((x) => ({name: x.description, value: x.id}));
    });
    this.commonService.getAgencies().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.agencies = response.map((x) => ({name: x.name, value: x.id}));
    });
    this.commonService.getShowrooms().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.showrooms = response.map((x) => ({name: x.name, value: x.id}));
    });
    this.orderService.getStates().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.states = response.map((x) => ({name: x[1], value: x[0]}));
    });
    this.orderService.getCategories().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.categories = response.map((x) => ({name: x.name, value: x.id}));
    });
    this.commonService.countryList().pipe(takeUntil(this.destroy$)).pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.countries = response.map((x) => ({name: x.description, value: x.id}));
    });
    this.commonService.channelList().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.channels = response.map((x) => ({name: x.description, value: x.id}));
    });
    this.commonService.shopVendorList().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.shopVendors = response.map((x) => ({name: x.name, value: x.id}));
    });
    this.orderService.getEtoStates().pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.etoStates = response.map((x) => ({name: x[1], value: x[0]}));
    });
  }

  private searchOnSelectionChange(): void {
    const getValueChangeOfForm = (controlName: string) => this.filterAreaForm.get(controlName)?.valueChanges || EMPTY;
    const getValueChangeObservableOfControlsName = (...controlNames: string[]) =>
      merge(...controlNames.map((c) => getValueChangeOfForm(c)));

    getValueChangeObservableOfControlsName(
      'intercompanyId',
      'agencyId',
      'showroomId',
      'customerChannelId',
      'countryId',
      'etoStatus',
      'categoryId',
      'status',
    ).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.search();
    });
  }

}
